import { Component, EventEmitter, Input, Output, OnInit, OnChanges, ViewChild, ElementRef, AfterViewInit, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileUploadComponent } from '../../../../shared/components/file-upload/file-upload.component';

export interface QuestionSubmission {
  question: string;
  screenshots: File[];
}

@Component({
  selector: 'app-question-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FileUploadComponent],
  template: `
    <form [formGroup]="questionForm" (ngSubmit)="onSubmit()" class="flex items-start space-x-3 w-full">
      <div class="flex-1 relative w-full">
        <label for="query" class="sr-only">Your question</label>
        <textarea
          #textareaInput
          id="query"
          formControlName="question"
          rows="5"
          (keydown)="onKeyDown($event)"
          (input)="autoResize()"
          [disabled]="isSubmitting || feedbackGiven"
          class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-vodafone-red focus:ring-vodafone-red resize-none text-sm sm:text-base min-h-20 sm:min-h-32"
          [ngClass]="{'bg-gray-100': feedbackGiven, 'opacity-70': feedbackGiven, 'cursor-not-allowed': feedbackGiven}"
          placeholder="What's your problem? Describe it here..."
          style="max-height: 33vh; overflow-y: auto;"
        ></textarea>
      </div>
      <div class="flex-shrink-0 self-end">
        <button
          type="submit"
          [disabled]="!questionForm.valid || isSubmitting || feedbackGiven"
          class="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-lg shadow-sm text-white bg-vodafone-red hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-red disabled:opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </div>
    </form>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      transition: height 0.2s ease-in-out;
    }
  `]
})
export class QuestionFormComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() isSubmitting = false;
  @Input() feedbackGiven = false;
  @Output() submitQuestion = new EventEmitter<QuestionSubmission>();
  @Output() heightChange = new EventEmitter<number>();
  @ViewChild('textareaInput') textareaInput!: ElementRef;
  @HostBinding('style.height') componentHeight = 'auto';

  questionForm: FormGroup;
  screenshots: File[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private lastHeight = 0;

  constructor(private fb: FormBuilder) {
    this.questionForm = this.fb.group({
      question: [
        { value: '', disabled: false },
        [Validators.required]
      ],
    });
  }

  ngOnInit() {
    this.updateFormState();
  }

  ngAfterViewInit() {
    // Set initial height
    setTimeout(() => this.autoResize(), 0);
    
    // Create a ResizeObserver to handle dynamic content changes
    if (typeof ResizeObserver !== 'undefined' && this.textareaInput?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.autoResize();
      });
      this.resizeObserver.observe(this.textareaInput.nativeElement);
    }
  }
  
  ngOnDestroy() {
    // Cleanup ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  ngOnChanges() {
    this.updateFormState();
  }

  private updateFormState() {
    const control = this.questionForm.get('question');
    if (control) {
      (this.isSubmitting || this.feedbackGiven) ? control.disable() : control.enable();
    }
  }

  autoResize() {
    const textarea = this.textareaInput?.nativeElement;
    if (!textarea) return;
    
    // Reset height temporarily to get the correct scrollHeight
    const originalOverflow = textarea.style.overflow;
    textarea.style.overflow = 'hidden';
    textarea.style.height = 'auto';
    
    // Set new height based on scrollHeight but limit to 1/3 of viewport height
    const maxHeight = window.innerHeight * 0.33;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
    
    // Restore overflow
    textarea.style.overflow = originalOverflow;
    
    // Calculate form height for parent container
    const form = textarea.closest('form');
    if (form) {
      const formHeight = form.offsetHeight;
      if (this.lastHeight !== formHeight) {
        this.lastHeight = formHeight;
        this.componentHeight = `${formHeight}px`;
        this.heightChange.emit(formHeight);
      }
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // Allow Enter for new lines with Shift key
    if (event.key === 'Enter' && !event.shiftKey && !this.isSubmitting && !this.feedbackGiven && this.questionForm.valid) {
      event.preventDefault();
      event.stopPropagation(); // Prevent duplicate submits
      this.onSubmit();
    }
  }

  onFilesChange(files: File[]): void {
    this.screenshots = files;
  }

  onSubmit(): void {
    if (this.questionForm.valid && !this.isSubmitting && !this.feedbackGiven) {
      const question = this.questionForm.get('question')?.value;
      if (question) {
        this.submitQuestion.emit({
          question,
          screenshots: this.screenshots,
        });
        
        // Reset form height after submit
        setTimeout(() => {
          this.questionForm.reset();
          this.autoResize();
        }, 0);
      }
    }
  }
}
